// apps/admin-ui/src/hooks/useDashboard.ts
// Dashboard hook with RBAC-aware widget query control
// ALL RIGHTS RESERVED

/**
 * Dashboard Hook
 * Fetches and manages dashboard data with:
 * - Auto-refresh per widget configuration
 * - Pause when widget is collapsed
 * - RBAC-filtered data fetching
 * 
 * FIXED 2026-02-01: Infinite re-render loop caused by unstable dependencies
 * FIXED 2026-02-01: refreshInterval was in seconds but setInterval expects ms
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api, DashboardData, SystemStatus } from '../api/client';
import { useVisibleWidgets } from './useVisibleWidgets';
import type { WidgetConfig } from '../config/dashboardWidgets';

// =============================================================================
// TYPES
// =============================================================================

export interface WidgetState {
  expanded: boolean;
  lastRefresh: Date | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseDashboardResult {
  // Data
  dashboard: DashboardData | null;
  systemStatus: SystemStatus | null;
  
  // Widget states
  widgetStates: Record<string, WidgetState>;
  
  // Visible widgets (RBAC filtered)
  visibleWidgets: WidgetConfig[];
  
  // Global state
  isLoading: boolean;
  error: string | null;
  lastRefresh: Date | null;
  
  // Actions
  refresh: () => Promise<void>;
  refreshWidget: (widgetId: string) => Promise<void>;
  setWidgetExpanded: (widgetId: string, expanded: boolean) => void;
}

// =============================================================================
// HOOK
// =============================================================================

export function useDashboard(): UseDashboardResult {
  // Data state
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Widget states
  const [widgetStates, setWidgetStates] = useState<Record<string, WidgetState>>({});

  // Get visible widgets based on user's RBAC permissions
  const { widgets: visibleWidgets } = useVisibleWidgets();

  // Interval refs for each widget
  const intervalRefs = useRef<Record<string, number>>({});
  
  // Track initialized widget IDs to prevent re-initialization loop
  const initializedWidgetIds = useRef<string>('');

  // FIX: Create stable widget ID string for comparison
  const widgetIdString = useMemo(() => 
    visibleWidgets.map(w => w.id).sort().join(','),
    [visibleWidgets]
  );

  // Initialize widget states when visible widgets change
  // FIX: Only run when widget IDs actually change, not on every render
  useEffect(() => {
    if (widgetIdString === initializedWidgetIds.current) {
      return; // Widget IDs haven't changed, skip
    }
    
    initializedWidgetIds.current = widgetIdString;
    
    setWidgetStates(prev => {
      const next: Record<string, WidgetState> = {};
      for (const widget of visibleWidgets) {
        next[widget.id] = prev[widget.id] || {
          expanded: widget.defaultExpanded,
          lastRefresh: null,
          isLoading: false,
          error: null,
        };
      }
      return next;
    });
  }, [widgetIdString, visibleWidgets]);

  // ==========================================================================
  // DATA FETCHING
  // ==========================================================================

  const refresh = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const [dashboardData, statusData] = await Promise.all([
        api.getDashboard(),
        api.getSystemStatus(),
      ]);

      setDashboard(dashboardData);
      setSystemStatus(statusData);
      setLastRefresh(new Date());

      // Update all widget lastRefresh times
      setWidgetStates(prev => {
        const next = { ...prev };
        for (const id of Object.keys(next)) {
          next[id] = { ...next[id], lastRefresh: new Date(), error: null };
        }
        return next;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(message);
      console.error('[useDashboard] Error:', message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshWidget = useCallback(async (widgetId: string) => {
    setWidgetStates(prev => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], isLoading: true, error: null },
    }));

    try {
      // For now, refresh all data (in future, could have per-widget endpoints)
      const [dashboardData, statusData] = await Promise.all([
        api.getDashboard(),
        api.getSystemStatus(),
      ]);

      setDashboard(dashboardData);
      setSystemStatus(statusData);

      setWidgetStates(prev => ({
        ...prev,
        [widgetId]: { 
          ...prev[widgetId], 
          isLoading: false, 
          lastRefresh: new Date(),
          error: null,
        },
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh';
      setWidgetStates(prev => ({
        ...prev,
        [widgetId]: { ...prev[widgetId], isLoading: false, error: message },
      }));
    }
  }, []);

  // ==========================================================================
  // WIDGET EXPANSION CONTROL
  // ==========================================================================

  const setWidgetExpanded = useCallback((widgetId: string, expanded: boolean) => {
    setWidgetStates(prev => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], expanded },
    }));
  }, []);

  // ==========================================================================
  // AUTO-REFRESH PER WIDGET
  // ==========================================================================

  // FIX: Create stable expanded state string for comparison
  const expandedStateString = useMemo(() => {
    return Object.entries(widgetStates)
      .filter(([_, state]) => state?.expanded)
      .map(([id]) => id)
      .sort()
      .join(',');
  }, [widgetStates]);

  useEffect(() => {
    // Clear all existing intervals
    for (const intervalId of Object.values(intervalRefs.current)) {
      clearInterval(intervalId);
    }
    intervalRefs.current = {};

    // Set up intervals for expanded widgets
    for (const widget of visibleWidgets) {
      const state = widgetStates[widget.id];
      
      // Only set interval if widget is expanded and has a refresh interval
      if (state?.expanded && widget.refreshInterval > 0) {
        // FIX: Convert seconds to milliseconds (* 1000)
        // refreshInterval is defined in seconds in widget config
        const intervalMs = widget.refreshInterval * 1000;
        
        intervalRefs.current[widget.id] = window.setInterval(() => {
          refreshWidget(widget.id);
        }, intervalMs);
      }
    }

    // Cleanup on unmount
    return () => {
      for (const intervalId of Object.values(intervalRefs.current)) {
        clearInterval(intervalId);
      }
    };
  // FIX: Use stable strings for comparison instead of object references
  }, [widgetIdString, expandedStateString, refreshWidget, visibleWidgets, widgetStates]);

  // Initial load - only run once
  const hasLoaded = useRef(false);
  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      refresh();
    }
  }, [refresh]);

  // ==========================================================================
  // RETURN
  // ==========================================================================

  return {
    dashboard,
    systemStatus,
    widgetStates,
    visibleWidgets,
    isLoading,
    error,
    lastRefresh,
    refresh,
    refreshWidget,
    setWidgetExpanded,
  };
}

